// src/components/delivery/DeliveryOrderList.jsx
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { 
  fetchAssignedOrders, 
  updateOrderStatus, 
  schedulePickup, 
  markReturnInTransit, 
  markBookReturned, 
  clearOrders,
  verifyOtpAndDeliver
} from "../../features/deliverySlice";
import DeliveryOrderCard from "./DeliveryOrderCard";
import DeliveryOrderDetails from "./DeliveryOrderDetails";
import { Search, Filter, Calendar } from "react-feather";
import { STATUS_FILTER_OPTIONS } from "../../constants/deliveryConstants";
import toast from "react-hot-toast";
import { useDebounce } from "../../hooks/useDebounce";

const DeliveryOrderList = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { orders } = useSelector((state) => state.delivery);
  
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "ALL");
  const [dateFilter, setDateFilter] = useState(searchParams.get("dateFilter") || "all");
  const [customStartDate, setCustomStartDate] = useState(searchParams.get("startDate") || "");
  const [customEndDate, setCustomEndDate] = useState(searchParams.get("endDate") || "");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  
  const debouncedSearch = useDebounce(search, 500);
  const observerTarget = useRef(null);

  // Date filter helper functions
  const getDateRange = (filter) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);

    switch (filter) {
      case "today":
        return { startDate: today, endDate: endOfToday };
      
      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const endOfYesterday = new Date(yesterday);
        endOfYesterday.setHours(23, 59, 59, 999);
        return { startDate: yesterday, endDate: endOfYesterday };
      
      case "thisWeek":
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        return { startDate: startOfWeek, endDate: endOfToday };
      
      case "thisMonth":
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return { startDate: startOfMonth, endDate: endOfToday };
      
      case "custom":
        if (customStartDate && customEndDate) {
          const startDate = new Date(customStartDate);
          startDate.setHours(0, 0, 0, 0);
          
          const endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
          
          return { startDate, endDate };
        }
        return { startDate: null, endDate: null };
      
      case "all":
      default:
        return { startDate: null, endDate: null };
    }
  };

  // Get current date range for API calls
  const currentDateRange = useMemo(() => getDateRange(dateFilter), [dateFilter, customStartDate, customEndDate]);

  // Load orders
  const loadOrders = useCallback(
    async (pageNum = 0, append = false) => {
      try {
        let statusParam = "";
        // For ALL, load all orders and filter on frontend
        if (statusFilter === "ALL") {
          statusParam = "";
        } else {
          // For specific status, send to backend
          statusParam = statusFilter;
        }
        
        // Prepare date parameters
        const dateParams = {};
        if (currentDateRange.startDate) {
          dateParams.startDate = currentDateRange.startDate.toISOString().split('T')[0];
        }
        if (currentDateRange.endDate) {
          dateParams.endDate = currentDateRange.endDate.toISOString().split('T')[0];
        }
        
        const result = await dispatch(
          fetchAssignedOrders({
            page: pageNum,
            size: 20,
            search: debouncedSearch,
            status: statusParam,
            startDate: dateParams.startDate,
            endDate: dateParams.endDate,
          })
        ).unwrap();

        setHasMore(pageNum < result.totalPages - 1);
      } catch (error) {
        toast.error("Failed to load orders");
      }
    },
    [dispatch, debouncedSearch, statusFilter, currentDateRange]
  );

  // Initial load and filter changes
  useEffect(() => {
    dispatch(clearOrders());
    setPage(0);
    loadOrders(0);
  }, [debouncedSearch, statusFilter]);

  // Update URL params
  useEffect(() => {
    const params = {};
    if (search) params.search = search;
    if (statusFilter !== "ALL") params.status = statusFilter;
    if (dateFilter !== "today") params.dateFilter = dateFilter;
    if (customStartDate) params.startDate = customStartDate;
    if (customEndDate) params.endDate = customEndDate;
    setSearchParams(params);
  }, [search, statusFilter, dateFilter, customStartDate, customEndDate, setSearchParams]);

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !orders.loading) {
          const nextPage = page + 1;
          setPage(nextPage);
          loadOrders(nextPage, true);
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, orders.loading, page, loadOrders]);

  const handleUpdateStatus = async (orderId, newStatus, currentStatus) => {
    try {
      // Use appropriate endpoint based on status transition
      if (newStatus === 'PICKUP_SCHEDULED' && currentStatus === 'RETURN_REQUESTED') {
        await dispatch(schedulePickup({ orderId })).unwrap();
      } else if (newStatus === 'RETURN_IN_TRANSIT' && currentStatus === 'PICKUP_SCHEDULED') {
        await dispatch(markReturnInTransit({ orderId })).unwrap();
      } else if (newStatus === 'RETURNED' && currentStatus === 'RETURN_IN_TRANSIT') {
        // For final return, we need to collect the delivery partner's name
        const deliveryPartnerName = prompt("Enter your name to confirm collection:");
        if (!deliveryPartnerName || !deliveryPartnerName.trim()) {
          toast.error("Collection confirmation required");
          return;
        }
        await dispatch(markBookReturned({ orderId, collectedBy: deliveryPartnerName.trim() })).unwrap();
      } else {
        // For forward delivery flow (PROCESSING -> SHIPPED -> ON_THE_WAY -> BOOK_WITH_ME)
        await dispatch(updateOrderStatus({ orderId, status: newStatus })).unwrap();
      }
      toast.success("Order status updated successfully");
      // Refresh current page
      loadOrders(0);
    } catch (error) {
      toast.error(error || "Failed to update order status");
    }
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
  };

  const handleCloseDetails = () => {
    setSelectedOrder(null);
  };

  const handleVerifyOtp = async (orderId, otp) => {
    try {
      await dispatch(verifyOtpAndDeliver({ orderId, otp })).unwrap();
      toast.success("Delivery verified successfully! 🎉");
      loadOrders(0);
    } catch (error) {
      throw error; // Re-throw to let the modal handle the error display
    }
  };

  // Date filter handlers
  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    if (filter !== "custom") {
      setCustomStartDate("");
      setCustomEndDate("");
    }
    dispatch(clearOrders());
    setPage(0);
  };

  const handleCustomDateChange = (startDate, endDate) => {
    setCustomStartDate(startDate);
    setCustomEndDate(endDate);
    if (startDate && endDate) {
      dispatch(clearOrders());
      setPage(0);
    }
  };

  const clearAllFilters = () => {
    setSearch("");
    setStatusFilter("ALL");
    setDateFilter("today");
    setCustomStartDate("");
    setCustomEndDate("");
    dispatch(clearOrders());
    setPage(0);
  };

  // Filter orders based on status - exclude return orders from Orders tab
  const filteredOrders = React.useMemo(() => {
    // Define forward delivery statuses (non-return orders)
    const forwardDeliveryStatuses = ["PROCESSING", "SHIPPED", "ON_THE_WAY", "BOOK_WITH_ME"];
    
    // Always filter out return-related statuses from Orders tab
    const nonReturnOrders = orders.content.filter(order => 
      forwardDeliveryStatuses.includes(order.deliveryStatus)
    );
    
    if (statusFilter === "PENDING") {
      // Show only non-completed forward delivery orders
      return nonReturnOrders.filter(order => 
        order.deliveryStatus !== "BOOK_WITH_ME" && 
        order.deliveryStatus !== "CANCELLED"
      );
    }
    
    return nonReturnOrders;
  }, [orders.content, statusFilter]);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-100">
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
            {/* Search */}
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Search by order, book, customer, or address..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition text-sm"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <Filter
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                size={18}
              />
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  dispatch(clearOrders());
                  setPage(0);
                }}
                className="w-full pl-10 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary appearance-none bg-white cursor-pointer transition text-sm font-medium"
              >
                {STATUS_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Filter */}
            <div className="relative">
              <Calendar
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                size={18}
              />
              <select
                value={dateFilter}
                onChange={(e) => handleDateFilterChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary appearance-none bg-white cursor-pointer transition text-sm font-medium"
              >
                <option value="all">All</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="thisWeek">This Week</option>
                <option value="thisMonth">This Month</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
          </div>

          {/* Custom Date Range */}
          {dateFilter === "custom" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => handleCustomDateChange(e.target.value, customEndDate)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => handleCustomDateChange(customStartDate, e.target.value)}
                  min={customStartDate}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
            </div>
          )}
          
          {/* Search Help Text */}
          {search && (
            <div className="flex items-start space-x-2 text-xs text-gray-600 bg-blue-50 px-3 py-2 rounded-lg border border-blue-100">
              <Search size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
              <p>
                <span className="font-semibold text-blue-700">Searching in:</span> Order ID, Book name, Customer name, Full address, City, Pincode
              </p>
            </div>
          )}
        </div>

        {/* Results Count and Active Filters */}
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm text-gray-600">
            {filteredOrders.length > 0 ? (
              <span className="font-medium">
                Showing <span className="text-primary">{filteredOrders.length}</span> of {orders.totalElements} orders
              </span>
            ) : (
              <span>No orders found</span>
            )}
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            {/* Active Filters Display */}
            {statusFilter !== "ALL" && (
              <span className="text-xs bg-primary/10 text-primary px-3 py-1 rounded-full font-medium">
                Status: {STATUS_FILTER_OPTIONS.find(opt => opt.value === statusFilter)?.label}
              </span>
            )}
            {dateFilter !== "today" && (
              <span className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium">
                {dateFilter === "custom" && customStartDate && customEndDate
                  ? `${customStartDate} to ${customEndDate}`
                  : `Date: ${dateFilter.charAt(0).toUpperCase() + dateFilter.slice(1).replace(/([A-Z])/g, ' $1')}`}
              </span>
            )}
            
            {/* Clear All Filters Button */}
            {(statusFilter !== "ALL" || dateFilter !== "today" || search) && (
              <button
                onClick={clearAllFilters}
                className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium hover:bg-gray-200 transition"
              >
                Clear All
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {orders.loading && page === 0 ? (
          // Initial loading skeleton
          <div className="space-y-4">
            {[...Array(5)].map((_, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow p-4 animate-pulse"
              >
                <div className="flex gap-4">
                  <div className="w-20 h-28 bg-gray-200 rounded-lg"></div>
                  <div className="flex-1 space-y-3">
                    <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    <div className="flex gap-2">
                      <div className="h-8 bg-gray-200 rounded w-20"></div>
                      <div className="h-8 bg-gray-200 rounded w-20"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredOrders.length === 0 ? (
          // Empty state
          <div className="bg-white rounded-xl shadow-sm p-12 sm:p-16 text-center border border-gray-100">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search size={40} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              No orders found
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {search || statusFilter !== "ALL" || dateFilter !== "today"
                ? "Try adjusting your search or filters to find orders"
                : "No orders at the moment."}
            </p>
            {(search || statusFilter !== "ALL" || dateFilter !== "today") && (
              <button
                onClick={clearAllFilters}
                className="px-6 py-2.5 bg-primary text-white rounded-lg hover:bg-orange-600 transition font-medium"
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <>
            {filteredOrders.map((order) => (
              <DeliveryOrderCard
                key={order.orderId}
                order={order}
                onUpdateStatus={handleUpdateStatus}
                onViewDetails={handleViewDetails}
              />
            ))}

            {/* Infinite scroll trigger */}
            {hasMore && (
              <div ref={observerTarget} className="py-4 text-center">
                {orders.loading && (
                  <div className="inline-block">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <DeliveryOrderDetails
          order={selectedOrder}
          onClose={handleCloseDetails}
          onUpdateStatus={handleUpdateStatus}
          onVerifyOtp={handleVerifyOtp}
        />
      )}
    </div>
  );
};

export default DeliveryOrderList;
